/**
 * The findings, worst first, each with the proof attached.
 *
 * The difference from the scanner's own list is the evidence link. The scanner
 * says "this site has broken links"; the brain says "this link, on this page,
 * answers 404" and lets the operator open it. That is the whole audit-first
 * method (docs/muugijuhis.md §7.2) in one row: a claim a prospect can check is
 * worth more than five they have to take on faith.
 *
 * The severity palette is the scanner's, imported rather than re-picked - two
 * lists in one CRM disagreeing about what "critical" looks like was a real bug
 * once and the shared file exists to stop it happening again.
 */
import { AlertTriangle, CheckCircle2, ExternalLink, Eye, FileText, Info, Ruler, ShieldAlert, Users } from "lucide-react";
import type { BrainFinding, BrainPeerComparison, BrainSeverity } from "../../../../lib/siteBrain";
import { SEVERITY_COLOUR } from "../../site-scanner/severity";
import type { T } from "../../site-scanner/types";
import { findingCategory, findingText } from "./findingText";

const ICON: Record<BrainSeverity, typeof Info> = {
  critical: ShieldAlert,
  warning: AlertTriangle,
  info: Info,
  good: CheckCircle2,
};

export function BrainFindings({
  findings,
  comparisons = [],
  t,
}: {
  findings: BrainFinding[];
  /** Field comparisons by finding kind. Empty is the ordinary case. */
  comparisons?: BrainPeerComparison[];
  t: T;
}) {
  if (!findings.length) {
    return (
      <p className="flex items-center gap-2 text-sm" style={{ color: "var(--color-success)" }}>
        <CheckCircle2 className="size-4" /> {t("brain.findings.clean")}
      </p>
    );
  }

  // Keyed by kind rather than by finding id: a site with eleven dead links has
  // eleven findings and one ratio, and the ratio belongs under each of them.
  const byKind = new Map(comparisons.map((c) => [c.kind, c]));

  return (
    <ul className="space-y-1.5">
      {findings.map((f) => (
        <FindingRow key={f.id} finding={f} comparison={byKind.get(f.kind) ?? null} t={t} />
      ))}
    </ul>
  );
}

/** Where an AI sentence came from, so nobody mistakes it for a measurement. */
const SOURCE_ICON: Partial<Record<BrainFinding["source"], typeof Info>> = {
  content: FileText,
  vision: Eye,
};

function FindingRow({
  finding,
  comparison,
  t,
}: {
  finding: BrainFinding;
  comparison: BrainPeerComparison | null;
  t: T;
}) {
  const Icon = ICON[finding.severity];
  const colour = SEVERITY_COLOUR[finding.severity];
  const url = finding.evidence.url;
  const category = findingCategory(finding, t);
  const SourceIcon = SOURCE_ICON[finding.source];

  return (
    <li
      className="flex items-start gap-2.5 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5"
      style={{ boxShadow: `inset 3px 0 0 0 color-mix(in oklab, ${colour} 65%, transparent)` }}
    >
      <Icon className="mt-0.5 size-4 shrink-0" style={{ color: colour }} />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">{findingText(finding, t)}</p>

        {/* A model's sentence wears its category and its origin. An operator
            about to repeat a claim on the phone has to be able to see, without
            asking, whether a machine measured it or a model read it. */}
        {category ? (
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            {SourceIcon ? <SourceIcon className="size-3 shrink-0" /> : null}
            <span className="truncate">{category}</span>
            <span aria-hidden>-</span>
            <span className="shrink-0">{t(`brain.source.${finding.source}`)}</span>
          </p>
        ) : null}

        {/* What the same field looks like. The strongest thing an operator can
            say on a call, and the one line here that is about somebody other
            than this company - so it carries counts and never a name. */}
        {comparison ? (
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="size-3 shrink-0" />
            <span>
              {t("brain.peers.line", {
                n: comparison.peersTotal,
                without: comparison.peersTotal - comparison.peersWith,
              })}
            </span>
          </p>
        ) : null}

        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-1 inline-flex max-w-full items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            <ExternalLink className="size-3 shrink-0" />
            <span className="truncate">{url}</span>
          </a>
        ) : null}
      </div>

      {/* A measured finding carries a number a machine produced. Marking it is
          what will keep the later vision pass honest: a picture is not a
          measurement, and its observations must never wear this. */}
      {finding.measured && finding.evidence.value ? (
        <span
          className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-xs tabular-nums text-muted-foreground"
          title={t("brain.findings.measured")}
        >
          <Ruler className="size-3" />
          {finding.evidence.value}
        </span>
      ) : null}
    </li>
  );
}
